/* global rwgps */
import cx from "classnames";
import Immutable from "immutable";
import { getConsole } from "lib/getConsole";
import _ from "lodash";
import React from "react";
import styles from "./ClimbAnimation.scss";
import { ClimbMetrics } from "./ClimbMetrics";
import { ElevationGraph } from "./ElevationGraph";
const console = getConsole("rr", "ClimbAnimation");

// data we need:
// 0. trip id
// 1. start and end of climb segment
// 2. elevation, velocity, heart rate, grade
// 3. is the climb block visible? (i.e. fully scrolled into view)
const memSlice = _.memoize(
  (points, start, end) => points.slice(start, end),
  (points, start, end) => "" + points.length + start + end
);

export const getMetricsFromPoints = _.memoize((points) => {
  console.log("‼️ metrics calculation");
  const tpsResult = rwgps.TrackPoint.fromJSON(Immutable.Iterable.isIterable(points) ? points.toJS() : points);

  const tpObjs = tpsResult.trackPoints;
  const data = new rwgps.Data(tpObjs);
  const mc = new rwgps.MetricsCalculator(data, false);
  const stats = mc.calculate();
  // smooth out data
  mc.calculateGradeAndSpeed();
  // we need a minimum elevation range of 90m for the graph to look reasonable
  if (stats.ele._max - stats.ele._min < 90) {
    stats.ele._max += 90 - (stats.ele._max - stats.ele._min);
  }
  // only return points with valid elevation data
  return { stats, points: mc._points.filter((p) => isFinite(p.ele)) };
});

// build a t-x-y mapping of time to distance to elevation, with a resolution of N points.
// Complexity is O(n), where n refers to points, not resolution.
// Could probably get it to somewhere around O(log(n)) if I implement binary search
// instead of iteration in the lastExaminedPoint while loop.
// todo memoize with resolution as well as points
export const getTimeIndexedData = _.memoize((points, resolution) => {
  console.log("‼️ getTimeIndexedData calculation ran");

  if (resolution > points.length) {
    throw Error("can't upconvert fewer points than resolution");
  }

  const tMin = points[0].time;
  const tMax = points[points.length - 1].time;
  const tRange = tMax - tMin;
  // we can safely assume here that the points are ordered by time
  let lastExaminedPoint = 0;
  return _.range(resolution) // generate a range of 0 to resolution -1
    .map((i) => {
      const time = tMin + (i / (resolution - 1)) * tRange;
      // now the tricky part. Need to find the first point in the data where point.distance > distance
      while (points[lastExaminedPoint].time < time) {
        lastExaminedPoint += 1;
      }
      // no interpolation if distance is an exact match
      if (time === points[lastExaminedPoint].time) {
        const { ele, distance, speed, grade } = points[lastExaminedPoint];
        return { ele, distance, speed, grade };
      } else {
        const prevPoint = points[lastExaminedPoint - 1];
        const nextPoint = points[lastExaminedPoint];
        const range = nextPoint.time - prevPoint.time;
        // time-weighted average
        const timeWeighted = (prop) =>
          (prevPoint[prop] * (range - (time - prevPoint.time))) / range +
          (nextPoint[prop] * (range - (nextPoint.time - time))) / range;

        return {
          ele: timeWeighted("ele"),
          distance: timeWeighted("distance"),
          speed: timeWeighted("speed"),
          grade: timeWeighted("grade"),
        };
      }
    });
});

const RUNNING = "RUNNING";
const STOP_PENDING = "STOP_PENDING";
const STOPPED = "STOPPED";

export class ClimbAnimation extends React.PureComponent {
  static ANIMATION_LENGTH = 30 * 1000; // duration of loop in ms
  static TICK_RATE = 33; // aims for about 30fps
  // some jerkiness may be caused by a low point count rather than dropped
  // frames. e.g. if we only have 200 data points and need to display at 600px,
  // things are going to, at a minimum, jump by 3px for each horizontal movement.
  // Might be worth thinking about revisiting data upscaling in the interpolation
  // functions.
  static TICK_INCREMENT = 1 / (ClimbAnimation.ANIMATION_LENGTH / ClimbAnimation.TICK_RATE);
  // if resolution ever becomes dynamic, you will need to update the memoization
  // hash function in the functions above
  static RENDER_RESOLUTION = 400;

  constructor(props) {
    super(props);
    this.state = { progress: 0 };
    this._progress = 0;
    this._animationLoopState = STOPPED;
    this._tickTimer = null;
  }

  componentDidMount() {
    // this governs the speed at which the physics model updates.
    const { paused } = this.props;
    if (!paused) {
      this.play();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.paused && !this.props.paused) {
      this.play();
    } else if (!prevProps.paused && this.props.paused) {
      this.pause();
    }
  }

  componentWillUnmount() {
    this.pause(); // clean up references
  }

  // physics loop
  tick = () => {
    const progress = this._progress + ClimbAnimation.TICK_INCREMENT;
    this._progress = progress < 1 ? progress : progress - 1;
  };

  // render loop
  // how to make sure there's only one of these recursively running, ever? Kinda tricky?
  animate = () => {
    this.setState({ progress: this._progress }, () => {
      if (this._animationLoopState === RUNNING) {
        // also, I didn't really think about this but if we're executing in an
        // environment that doesn't support tail call optimization this could
        // blow up
        window.requestAnimationFrame(this.animate);
      } else if (this._animationLoopState === STOP_PENDING) {
        this._animationLoopState = STOPPED;
      }
    });
  };

  play = () => {
    // it's important that this is a noop if we're already playing
    console.log("ClimbAnimation: play");
    // start the physics loop if it's not running
    if (!this._tickTimer) {
      this._tickTimer = setInterval(this.tick, ClimbAnimation.TICK_RATE);
    }
    switch (this._animationLoopState) {
      case STOPPED:
        this._animationLoopState = RUNNING;
        this.animate();
        break;
      case STOP_PENDING:
        this._animationLoopState = RUNNING;
        break;
    }
  };

  pause = () => {
    // it's important that this is a noop if we're already paused
    console.log("ClimbAnimation: pause");
    // stop the physics loop
    if (this._tickTimer) {
      clearInterval(this._tickTimer);
      this._tickTimer = null;
    }
    if (this._animationLoopState === RUNNING) {
      this._animationLoopState = STOP_PENDING;
    }
  };

  render() {
    const { trackPoints, highlightSlice, metricUnits } = this.props;
    const { progress } = this.state;
    const [start, end] = highlightSlice;
    // it's important that all the expensive computation here is memoized
    const slice = memSlice(trackPoints, start, end);
    const { points, stats } = getMetricsFromPoints(slice);
    const resolution = Math.min(ClimbAnimation.RENDER_RESOLUTION, points.length);
    const timeIndexed = getTimeIndexedData(points, resolution);
    const { paused } = this.props;
    return (
      <div className={cx(styles.ClimbAnimation, paused && styles.ClimbAnimationPaused)}>
        <div className={styles.left}>
          <ElevationGraph
            height={200}
            resolution={resolution}
            points={points}
            timeIndexed={timeIndexed}
            stats={stats}
            progress={progress}
            paused={paused}
          />
        </div>
        <div className={styles.right}>
          <ClimbMetrics metricUnits={metricUnits} points={points} timeIndexed={timeIndexed} progress={progress} />
        </div>
      </div>
    );
  }
}
